import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalExtensionComposer } from "@lexical/react/LexicalExtensionComposer";
import { HistoryExtension } from "@lexical/history";
import {
  configExtension,
  defineExtension,
  HISTORY_MERGE_TAG,
  type AnyLexicalExtension,
  type LexicalEditor,
} from "lexical";
import {
  lazy,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useSyncExternalStore,
  type JSX,
  type Ref,
} from "react";
import { $changedBlockIds } from "../features/block-id/changes";
import {
  $placeBlocks,
  ClipboardExtension,
  type PasteAdjustment,
  type PasteRejection,
} from "../features/clipboard/extension";
import { ComposerSubmitExtension } from "../features/composer/extension";
import type { AttachmentRejection } from "../features/attachments/commands";
import type { Collaboration } from "../features/collab/contract";
import { coarsePointer } from "../features/composer/pointer";
import { HashtagsExtension } from "../features/hashtags/extension";
import { LinkActivateExtension } from "../features/host/links";
import { registerSelectionState } from "../features/host/selection";
import { LinkPolicyExtension } from "../features/links/extension";
import { MarkdownShortcutsExtension } from "../features/markdown/extension";
import { createSettler, replaceContent } from "../features/revisions/replace";
import { SuggestionsExtension } from "../features/suggestions/extension";
import { TokensExtension } from "../features/tokens/extension";
import {
  checkUrl,
  CORE_REGISTRY,
  createEmptyDocument,
  DEFAULT_LIMITS,
  DEFAULT_URL_POLICY,
  usesAttachments,
  usesTables,
  type AttachmentAdapter,
  type AttachmentRef,
  type CapabilityOverrides,
  type DocumentLimits,
  type DocumentId,
  type DocumentIssue,
  type EditorDocument,
  type EditorPreset,
  type InlineToken,
  type TokenRegistry,
  type UrlPolicy,
} from "../index";
import { createErrorWatch, parseContent } from "../lexical/parse";
import { PRESET_EXTENSIONS } from "../lexical/presets";
import { originFromTags } from "../lexical/tags";
import type { DocumentSession } from "../persistence/index";
import { AttachmentContext, type AttachmentHost } from "./attachment-context";
import { createTrayStore, type TrayStore } from "./attachment-tray";
import { attachmentsStatus, loadAttachments, type AttachmentsModule } from "./attachments";
import { BlockControls } from "./BlockControls";
import { CompatibilityState } from "./CompatibilityState";
import { ComposerChrome } from "./ComposerChrome";
import type { ComposerSlots } from "./composer-context";
import {
  clearDraft,
  createComposerSubmit,
  createPendingStore,
  isBlankMessage,
  readComposerMessage,
  type ComposerMessage,
  type PendingStore,
  type SubmitOutcome,
} from "./composer";
import type { EditorChange, EditorCommonProps, SuggestionOptions } from "./contract";
import { ChunkBoundary } from "./ChunkBoundary";
import { PasteNotice } from "./PasteNotice";
import {
  createLiveEditor,
  type DispatchResult,
  type EditorHandle,
  type EditorIntent,
  type LiveEditor,
} from "./handle";
import { createGhostStore, GhostText, type GhostStore } from "./GhostText";
import { DEFAULT_LABELS, type EditorLabels } from "./labels";
import { SelectionToolbar } from "./SelectionToolbar";
import { SuggestionMenu, useSuggestionAria } from "./SuggestionMenu";
import { loadTables, tablesStatus, type TablesModule } from "./tables";

// The table menu is its own chunk again: an editor that offers tables has the nodes from the
// start, and fetches the controls when the caret first enters a table.
const TableControls = lazy(() => import("./tables/TableControls"));
// What an attachment looks like. The node that holds one is in the chunk above it and knows nothing
// of React; this is what it is given to draw with, fetched alongside it.
const AttachmentView = lazy(() => import("./attachments/AttachmentView"));
const AttachmentNotice = lazy(() => import("./attachments/AttachmentNotice"));
// What joins a shared editor to its document. Only a shared editor fetches it.
const CollaborationBridge = lazy(() => import("./collab/CollaborationBridge"));

export interface AttachmentOptions {
  /** The host's asset store: where files are kept, and what says how their uploads stand. */
  adapter: AttachmentAdapter;
  /**
   * Somebody asked to open a file from its card. This reports which and nothing else: whether the
   * user may have it, and how it is shown or downloaded, is the host's decision. Without it a
   * card has no "Open".
   */
  onOpen?: (attachment: AttachmentRef) => void;
  /**
   * The kinds of file this editor takes, as the `accept` of a file input has them: `image/*`,
   * `application/pdf`, `.csv`. Everything, when left out. Read once per mount.
   */
  accept?: readonly string[];
  /**
   * A file that was dropped, pasted, or picked is not in the document: too large, not of a kind
   * `accept` takes, refused by the adapter, or staged after the editor that took it was gone (the
   * asset is then in your store and in no document). The editor says so itself as well.
   */
  onRejected?: (rejection: AttachmentRejection) => void;
}

export type { EditorChange, EditorCommonProps, SuggestionOptions };

/** Everything `<Editor>` takes: the common props, and the features it can be given. */
export interface EditorProps extends EditorCommonProps {
  /**
   * The product's token types. Pass the same registry to the session, which validates and upgrades
   * tokens when it opens the document. Read once per mount.
   */
  tokens?: TokenRegistry;
  /**
   * A token was clicked. This reports what it refers to and nothing else: whether the user may
   * open it is the host's decision.
   */
  onTokenActivate?: (token: InlineToken) => void;
  /**
   * A hashtag was clicked. Receives the tag's name without its hash: `launch` for `#launch`.
   * Hashtags are made as `#tag` is typed; `collectTags` lists a document's tags.
   */
  onTagActivate?: (tag: string) => void;
  /**
   * Whether this editor offers making tables: a "Table" row in the slash menu and the
   * `insert-table` intent. On by default in the document preset, off elsewhere. A table that is
   * already in the document loads and can be edited either way, in every preset. Table support is
   * fetched on demand, when this is on or the document holds a table, and never otherwise; see
   * `preloadTables`. Read once per mount.
   */
  tables?: boolean;
  /**
   * Images and files. With an adapter the editor has attachments: it shows the ones a document
   * holds from the host's store, with how their uploads stand. Without one, a document that holds
   * attachments still opens, and shows each by its stored name. Attachment support is fetched on
   * demand, when this is given or the document holds an attachment, and never otherwise; see
   * `preloadAttachments`. Files that are dropped, pasted, picked from the `/` menu, or given to
   * `insert-attachment` are kept by the adapter first and put in the document when it has them;
   * writing is never held up. Whether it is given at all is read once per mount.
   */
  attachments?: AttachmentOptions;
  /**
   * The most one paste, drop, or `insert-markdown` may bring in: bytes of the clipboard text, nodes,
   * and nesting. Anything over is refused whole and the document is left as it was. Defaults to
   * `DEFAULT_LIMITS`; keep it within the session's limits. Read once per mount.
   */
  pasteLimits?: DocumentLimits;
  /** A paste was refused for its size. The editor also says so itself, in a polite notice. */
  onPasteRejected?: (rejection: PasteRejection) => void;
  /**
   * A paste went in, changed to fit: merged cells split, a table inside a cell or a list turned
   * into text, a table turned into lines where this editor has none. No text is ever among the
   * changes. The editor says so itself as well.
   */
  onPasteAdjusted?: (adjustment: PasteAdjustment) => void;
  /**
   * Composer preset only. `always` shows a formatting toolbar above the text, with every mark and
   * block the composer offers, pressed where the selection is in one; the toolbar that floats over
   * a selection is then only the link field. `never` (the default) leaves formatting to that
   * floating toolbar, the shortcuts, and Markdown. It may change while the editor is mounted.
   */
  toolbar?: "always" | "never";
  /** Composer preset only. Your own content in the composer's chrome: see `ComposerSlots`. */
  slots?: ComposerSlots;
  /**
   * A shared document to join, from `createCollaboration` in `@zsherman/editor/collab`. What the editor
   * shows is then the shared document, not what the session has stored: the session keeps a local
   * copy of it, and its document is only ever used to seed a shared document that is empty. Undo
   * takes back this person's edits only, and `reload()` and `take-remote` rebuild the editor
   * instead of replacing its content, which would be an edit everyone receives. Pass the same
   * object on every render; another one rebuilds the editor.
   */
  collaboration?: Collaboration;
  ref?: Ref<EditorHandle>;
}

interface LoadStatus {
  subscribe(listener: () => void): () => void;
  get(): DocumentIssue | null;
  fail(issue: DocumentIssue): void;
}

function createLoadStatus(): LoadStatus {
  let issue: DocumentIssue | null = null;
  const listeners = new Set<() => void>();
  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    get: () => issue,
    fail(next) {
      issue = next;
      for (const listener of listeners) listener();
    },
  };
}

/**
 * Whether the document the session is showing holds something that loads on demand. One scan per
 * stored copy, however often the editor renders. `generation` is not used, and has to be passed:
 * this file is compiled by the React Compiler, which caches a call by its arguments, and the
 * session is one stable object whose document changes underneath it. Without an argument that
 * changes when the document does, the answer for the first document would be kept for the next.
 */
function createDocumentProbe(
  test: (root: unknown) => boolean,
): (session: DocumentSession, generation: number) => boolean {
  const answers = new WeakMap<EditorDocument, boolean>();
  return (session, generation) => {
    void generation;
    const document = session.getDocument();
    let answer = answers.get(document);
    if (answer === undefined) {
      answer = test(document.content.root);
      answers.set(document, answer);
    }
    return answer;
  };
}

const holdsTables = createDocumentProbe(usesTables);
const holdsAttachments = createDocumentProbe(usesAttachments);

// Sessions and collaborations are what an editor is built around: another one is another editor.
const identities = new WeakMap<object, number>();
let nextIdentity = 0;
function identityOf(value: object | undefined): number {
  if (value === undefined) return 0;
  let id = identities.get(value);
  if (id === undefined) {
    id = ++nextIdentity;
    identities.set(value, id);
  }
  return id;
}

export function Editor({
  session,
  preset = "document",
  readOnly = false,
  density = "comfortable",
  colorScheme = "auto",
  urlPolicy = DEFAULT_URL_POLICY,
  tokens,
  onTokenActivate,
  onTagActivate,
  suggestions,
  slashMenu,
  tables,
  attachments,
  blockHandles,
  pasteLimits,
  onPasteRejected,
  onPasteAdjusted,
  labels: labelOverrides,
  className,
  onChange,
  onSelectionChange,
  onLinkActivate,
  editableProps,
  onSubmit,
  isEmptyMessage,
  sendButton = "auto",
  toolbar = "never",
  slots,
  collaboration,
  onError,
  ref,
}: EditorProps): JSX.Element {
  const state = useSyncExternalStore(session.subscribe, session.getState, session.getState);
  const labels: EditorLabels = { ...DEFAULT_LABELS, ...labelOverrides };
  const liveRef = useRef<LiveEditor | null>(null);
  const [onLive] = useState(() => (live: LiveEditor | null): void => {
    liveRef.current = live;
  });
  // The handle outlives any one editor, and so does what it has been asked to show at the caret.
  const [ghost] = useState(createGhostStore);

  useEffect(() => {
    if (state.phase === "idle" && state.error === null) void session.load();
  }, [session, state.phase, state.error]);

  const handles = blockHandles ?? preset === "document";
  // Table support is needed by an editor that offers tables, and by any editor whose document has
  // one. Both are known before the editor is built, which is when Lexical needs its node types.
  // Not `readOnly`: that changes on a live editor, and this decides what the editor is built from.
  const offersTables = tables ?? CORE_REGISTRY.offers(preset, "table");
  // A shared document can be handed a table by anyone at any time, and an editor cannot be given
  // node types once it exists, so a shared editor always has them.
  const needsTables =
    offersTables ||
    collaboration !== undefined ||
    (state.phase === "ready" && holdsTables(session, state.generation));
  const tableSupport = useSyncExternalStore(
    tablesStatus.subscribe,
    tablesStatus.get,
    tablesStatus.get,
  );
  // Attachments load the same way and for the same reasons. No preset offers them by itself.
  const needsAttachments =
    attachments !== undefined ||
    collaboration !== undefined ||
    (state.phase === "ready" && holdsAttachments(session, state.generation));
  const attachmentSupport = useSyncExternalStore(
    attachmentsStatus.subscribe,
    attachmentsStatus.get,
    attachmentsStatus.get,
  );
  useEffect(() => {
    // Alongside the document's own load, not after it.
    if (needsTables) loadTables();
    if (needsAttachments) loadAttachments();
  }, [needsTables, needsAttachments]);
  // The part of the editor that did not arrive, if one did not, and what fetches it again.
  const missing =
    needsTables && tableSupport.phase === "failed"
      ? { error: tableSupport.error, retry: loadTables }
      : needsAttachments && attachmentSupport.phase === "failed"
        ? { error: attachmentSupport.error, retry: loadAttachments }
        : null;
  const partsReady =
    (!needsTables || tableSupport.phase === "ready") &&
    (!needsAttachments || attachmentSupport.phase === "ready");
  const [dispatch] = useState(
    () =>
      (intent: EditorIntent): DispatchResult =>
        liveRef.current?.dispatch(intent) ?? { status: "rejected", reason: "not-ready" },
  );

  useImperativeHandle(
    ref,
    (): EditorHandle => ({
      dispatch,
      getSnapshot() {
        if (session.getState().phase === "unreadable") return { ok: false, reason: "unreadable" };
        const live = liveRef.current;
        return live === null
          ? { ok: false, reason: "not-ready" }
          : { ok: true, document: live.snapshot() };
      },
      // Without an editor there is nothing to export, and an empty text would read as a document
      // that was emptied: whoever saves it would write the loss. It fails, as getSnapshot does.
      exportMarkdown: () => {
        const live = liveRef.current;
        return live === null
          ? Promise.reject(new Error("The editor is not ready: nothing to export."))
          : Promise.resolve(live.markdown());
      },
      exportPlainText: () => liveRef.current?.plainText() ?? "",
      pasteAsMarkdown: () =>
        liveRef.current?.pasteAsMarkdown() ??
        Promise.resolve({ status: "rejected", reason: "not-ready" }),
      flush: () => session.flush(),
      getEditSequence: () => session.getState().editSequence,
      getSelectedBlockIds: () => liveRef.current?.selectedBlockIds() ?? [],
      getSelectionInfo: () => liveRef.current?.selectionInfo() ?? null,
      setGhostText: (text) => ghost.set(text),
    }),
    [session, dispatch, ghost],
  );

  const rootClass = [
    "ze",
    handles ? "ze-with-handles" : "",
    `ze-${preset}`,
    `ze-${density}`,
    colorScheme === "auto" ? "" : `ze-${colorScheme}`,
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  let body: JSX.Element;
  if (state.phase === "unreadable" && state.unreadable !== null) {
    body = (
      <CompatibilityState
        documentId={session.documentId}
        raw={state.unreadable.raw}
        issue={state.unreadable.issue}
        labels={labels}
      />
    );
  } else if (state.phase === "ready" && missing !== null) {
    // The document is fine and so is what is stored; a part of the editor did not arrive.
    body = (
      <div className="ze-state ze-state-error" role="alert">
        <p className="ze-state-title">{labels.loadFailedTitle}</p>
        <p className="ze-state-detail">{missing.error.message}</p>
        <button type="button" className="ze-state-action" onClick={missing.retry}>
          {labels.retry}
        </button>
      </div>
    );
  } else if (state.phase === "ready" && partsReady) {
    body = (
      <Surface
        // What loads on demand is part of what the editor is built from, so gaining it is a
        // rebuild. That only happens to an editor that had no tables, or no attachments, and is
        // handed a document that has one. The preset is what the editor is built from, too.
        key={`${identityOf(session)}:${identityOf(collaboration)}:${preset}:${state.generation}:${needsTables ? "tables" : "plain"}:${needsAttachments ? "attachments" : "plain"}`}
        tablesModule={needsTables ? tableSupport.module : null}
        attachmentsModule={needsAttachments ? attachmentSupport.module : null}
        offersTables={offersTables}
        attachments={attachments}
        session={session}
        preset={preset}
        readOnly={readOnly}
        urlPolicy={urlPolicy}
        tokens={tokens}
        onTokenActivate={onTokenActivate}
        onTagActivate={onTagActivate}
        suggestions={suggestions}
        slashMenu={slashMenu ?? preset !== "composer"}
        blockHandles={handles}
        pasteLimits={pasteLimits}
        onPasteRejected={onPasteRejected}
        onPasteAdjusted={onPasteAdjusted}
        dispatch={dispatch}
        labels={labels}
        onLive={onLive}
        ghost={ghost}
        onChange={onChange}
        onSelectionChange={onSelectionChange}
        onLinkActivate={onLinkActivate}
        editableProps={editableProps}
        onSubmit={onSubmit}
        isEmptyMessage={isEmptyMessage}
        sendButton={sendButton}
        toolbar={toolbar}
        slots={slots}
        collaboration={collaboration}
        onError={onError}
      />
    );
  } else if (state.error !== null) {
    body = (
      <div className="ze-state ze-state-error" role="alert">
        <p className="ze-state-title">{labels.loadFailedTitle}</p>
        <p className="ze-state-detail">{state.error.message}</p>
        <button type="button" className="ze-state-action" onClick={() => void session.load()}>
          {labels.retry}
        </button>
      </div>
    );
  } else {
    body = (
      <div className="ze-state ze-state-loading" aria-busy="true">
        {labels.loading}
      </div>
    );
  }

  return (
    <div className={rootClass} data-phase={state.phase} data-save={state.save}>
      {body}
    </div>
  );
}

interface SurfaceProps {
  /** Table support, already loaded, when this editor is to have it. */
  tablesModule: TablesModule | null;
  /** Attachment support, already loaded, when this editor is to have it. */
  attachmentsModule: AttachmentsModule | null;
  offersTables: boolean;
  attachments: AttachmentOptions | undefined;
  session: DocumentSession;
  preset: EditorPreset;
  readOnly: boolean;
  urlPolicy: UrlPolicy;
  tokens: TokenRegistry | undefined;
  onTokenActivate: EditorProps["onTokenActivate"];
  onTagActivate: EditorProps["onTagActivate"];
  suggestions: SuggestionOptions | undefined;
  slashMenu: boolean;
  blockHandles: boolean;
  pasteLimits: DocumentLimits | undefined;
  onPasteRejected: EditorProps["onPasteRejected"];
  onPasteAdjusted: EditorProps["onPasteAdjusted"];
  dispatch: (intent: EditorIntent) => DispatchResult;
  labels: EditorLabels;
  onLive: (live: LiveEditor | null) => void;
  ghost: GhostStore;
  onChange: EditorProps["onChange"];
  onSelectionChange: EditorProps["onSelectionChange"];
  onLinkActivate: EditorProps["onLinkActivate"];
  editableProps: EditorProps["editableProps"];
  onSubmit: EditorProps["onSubmit"];
  isEmptyMessage: EditorProps["isEmptyMessage"];
  sendButton: NonNullable<EditorProps["sendButton"]>;
  toolbar: NonNullable<EditorProps["toolbar"]>;
  slots: EditorProps["slots"];
  collaboration: Collaboration | undefined;
  onError: EditorProps["onError"];
}

interface Callbacks {
  attachments: EditorProps["attachments"];
  onTokenActivate: EditorProps["onTokenActivate"];
  onTagActivate: EditorProps["onTagActivate"];
  onPasteRejected: EditorProps["onPasteRejected"];
  onPasteAdjusted: EditorProps["onPasteAdjusted"];
  onChange: EditorProps["onChange"];
  onSelectionChange: EditorProps["onSelectionChange"];
  onLinkActivate: EditorProps["onLinkActivate"];
  onSubmit: EditorProps["onSubmit"];
  isEmptyMessage: EditorProps["isEmptyMessage"];
  onError: EditorProps["onError"];
}

/**
 * The editor outlives any single render, so the things it calls back into live here rather than
 * in closures: the latest host callbacks, and the composer's submit action.
 */
class Wiring {
  callbacks: Callbacks;
  readonly submit: { current: (() => SubmitOutcome) | null } = { current: null };
  readonly pending: PendingStore = createPendingStore();
  readonly activate: { current: (token: InlineToken) => void } = {
    current: (token) => this.callbacks.onTokenActivate?.(token),
  };
  readonly activateTag: { current: (tag: string) => void } = {
    current: (tag) => this.callbacks.onTagActivate?.(tag),
  };
  readonly activateLink: { current: (url: string, event: MouseEvent) => boolean | void } = {
    current: (url, event) => this.callbacks.onLinkActivate?.(url, event),
  };
  readonly pasteRejected: { current: (rejection: PasteRejection) => void } = {
    current: (rejection) => this.callbacks.onPasteRejected?.(rejection),
  };
  readonly pasteAdjusted: { current: (adjustment: PasteAdjustment) => void } = {
    current: (adjustment) => this.callbacks.onPasteAdjusted?.(adjustment),
  };
  /** The host's asset store as of the latest render, read when files arrive. */
  readonly adapter: { current: AttachmentAdapter | null };
  /** A composer's attachments, which go beside its message. `null` in every other editor. */
  readonly tray: TrayStore | null;
  readonly attachmentRejected: { current: (rejection: AttachmentRejection) => void } = {
    current: (rejection) => this.callbacks.attachments?.onRejected?.(rejection),
  };
  /** The editor's `onError`, which whoever parses stored content watches while it does. */
  readonly errors = createErrorWatch((error) => this.callbacks.onError?.(error));

  constructor(callbacks: Callbacks, trayFor: DocumentId | null) {
    this.callbacks = callbacks;
    this.adapter = { current: callbacks.attachments?.adapter ?? null };
    this.tray = trayFor === null ? null : createTrayStore(trayFor, () => this.adapter.current);
  }

  update(callbacks: Callbacks): void {
    this.callbacks = callbacks;
    this.adapter.current = callbacks.attachments?.adapter ?? null;
  }

  setSubmit(submit: (() => SubmitOutcome) | null): void {
    this.submit.current = submit;
  }
}

function Surface(props: SurfaceProps): JSX.Element {
  const { session, preset, readOnly, urlPolicy, tokens, suggestions, slashMenu, labels } = props;
  const { onLive, collaboration } = props;
  const { onTokenActivate, onTagActivate, onPasteRejected, onPasteAdjusted } = props;
  const { onChange, onSelectionChange, onLinkActivate, onSubmit, isEmptyMessage, onError } = props;
  const callbacks: Callbacks = {
    attachments: props.attachments,
    onTokenActivate,
    onTagActivate,
    onPasteRejected,
    onPasteAdjusted,
    onChange,
    onSelectionChange,
    onLinkActivate,
    onSubmit,
    isEmptyMessage,
    onError,
  };
  const [wiring] = useState(
    () =>
      new Wiring(
        callbacks,
        preset === "composer" && props.attachments !== undefined ? session.documentId : null,
      ),
  );
  useEffect(() => {
    wiring.update(callbacks);
  });
  useEffect(() => wiring.tray?.restore(), [wiring]);
  const [send] = useState(() => (): void => void wiring.submit.current?.());
  const [policy] = useState(
    () => (): ((message: ComposerMessage) => boolean) =>
      wiring.callbacks.isEmptyMessage ?? isBlankMessage,
  );
  const coarse = useSyncExternalStore(coarsePointer.subscribe, coarsePointer.get, () => false);
  // Only the composer's row below renders it, so the preset needs no checking here.
  const showSend =
    !readOnly &&
    onSubmit !== undefined &&
    (props.sendButton === "always" || (props.sendButton === "auto" && coarse));

  // Built once per mount. The key on <Surface> decides when a new editor is needed: another
  // session, or stored content this editor could not take in place. History and selection never
  // carry over between documents. Another revision of the same document replaces the content of
  // this editor instead (see `replaceContent`), under the policy the host gave the session.
  const [setup] = useState(() => {
    const loadStatus = createLoadStatus();
    const document = session.getDocument();
    const validateUrl = (url: string): boolean => checkUrl(url, urlPolicy).ok;
    const limits = props.pasteLimits ?? DEFAULT_LIMITS;
    const attachments = props.attachmentsModule;
    const offers: CapabilityOverrides = {
      table: props.offersTables,
      // No preset offers attachments by itself: an editor has them when it is given a store.
      attachment: props.attachments !== undefined,
    };
    // The stored document, as the editor's first content. Alone, the editor starts with it. Shared,
    // it starts with nothing, and this is kept for seeding a shared document that is empty.
    const showStored = (editor: LexicalEditor): boolean => {
      const parsed = parseContent(editor, document.content, wiring.errors.watch);
      if (parsed.ok) {
        // Loading is not an edit: it merges into the empty history entry instead of adding one.
        editor.setEditorState(parsed.state, { tag: HISTORY_MERGE_TAG });
      } else {
        loadStatus.fail({ code: "malformed-node", message: parsed.error.message });
      }
      return parsed.ok;
    };
    const extension: AnyLexicalExtension = defineExtension({
      name: "z-editor/session",
      namespace: `z-editor:${session.documentId}`,
      // A shared editor is for writing in once its document has arrived, which its bridge decides.
      editable: !readOnly && collaboration === undefined,
      $initialEditorState: null,
      dependencies: [
        PRESET_EXTENSIONS[preset],
        configExtension(LinkPolicyExtension, { urlPolicy }),
        configExtension(LinkActivateExtension, { activate: wiring.activateLink, urlPolicy }),
        configExtension(MarkdownShortcutsExtension, { validateUrl }),
        configExtension(SuggestionsExtension, {
          documentId: session.documentId,
          providers: suggestions?.providers ?? [],
          commands: CORE_REGISTRY.commandsFor(preset, offers),
          slash: slashMenu,
          ...(suggestions?.debounceMs === undefined ? {} : { debounceMs: suggestions.debounceMs }),
          ...(suggestions?.cacheContext === undefined
            ? {}
            : { cacheContext: suggestions.cacheContext }),
        }),
        configExtension(TokensExtension, {
          activate: wiring.activate,
          ...(tokens === undefined ? {} : { registry: tokens }),
        }),
        configExtension(HashtagsExtension, { activate: wiring.activateTag }),
        configExtension(ClipboardExtension, {
          documentId: session.documentId,
          limits,
          urlPolicy,
          tokens: tokens ?? null,
          tables: props.tablesModule !== null,
          // A composer with a tray keeps attachments beside its message, and none in it.
          attachments: props.attachmentsModule !== null && wiring.tray === null,
          reject: wiring.pasteRejected,
          adjust: wiring.pasteAdjusted,
        }),
        ...(props.tablesModule === null
          ? []
          : [configExtension(props.tablesModule.TablesExtension, { limits })]),
        ...(attachments === null
          ? []
          : [
              configExtension(attachments.AttachmentsExtension, {
                // Given when the editor is built and never after: a node draws itself when it is
                // made or changed, and changing them all to hand over a renderer would be an edit.
                render: (nodeKey, attachment) => (
                  <AttachmentView
                    nodeKey={nodeKey}
                    attachment={attachment}
                    extension={attachments.AttachmentsExtension}
                  />
                ),
                documentId: session.documentId,
                adapter: wiring.adapter,
                limits,
                accept: props.attachments?.accept ?? [],
                reject: wiring.attachmentRejected,
                $place: $placeBlocks,
                tray: wiring.tray,
              }),
            ]),
        ...(preset === "composer"
          ? [configExtension(ComposerSubmitExtension, { submit: wiring.submit })]
          : []),
        // Lexical's history would take back other people's edits along with this person's. A
        // shared editor undoes through Yjs instead, which the collaboration sets up.
        ...(collaboration === undefined
          ? []
          : [configExtension(HistoryExtension, { disabled: true })]),
      ],
      onError: wiring.errors.onError,
      afterRegistration(editor) {
        if (collaboration === undefined) showStored(editor);
        return () => {};
      },
    });
    // The policy and registry the editor was built with. Everything else reads these, not the
    // latest props: both are read once per mount, and a host that makes a new object on every
    // render would otherwise re-attach the session (and reset a pending send) each time.
    return {
      extension,
      loadStatus,
      offers,
      showStored,
      urlPolicy,
      tokens,
      raw: JSON.stringify(document),
    };
  });
  const waiting = !useCollaborationReady(collaboration);
  const loadIssue = useSyncExternalStore(
    setup.loadStatus.subscribe,
    setup.loadStatus.get,
    setup.loadStatus.get,
  );

  if (loadIssue !== null) {
    // Validation passed but the engine still refused the content. Nothing is attached to the
    // session, so nothing can autosave over the stored document.
    return (
      <CompatibilityState
        documentId={session.documentId}
        raw={setup.raw}
        issue={loadIssue}
        labels={labels}
      />
    );
  }

  // What an attachment draws itself from. Attachments are mounted by the editor's decorators, which
  // keep the context of where the editor is, so this reaches them wherever they are.
  const attachmentHost: AttachmentHost = {
    adapter: props.attachments?.adapter ?? null,
    labels: labels.attachments ?? {},
    readOnly,
    onOpen: props.attachments?.onOpen,
  };

  return (
    <AttachmentContext value={attachmentHost}>
      <LexicalExtensionComposer extension={setup.extension} contentEditable={null}>
        {preset === "composer" ? (
          // The chrome is there whatever it shows, so the editable is never remounted when a pointer
          // is plugged in or taken away, or the host turns the toolbar on.
          <ComposerChrome
            documentId={session.documentId}
            labels={labels}
            toolbar={props.toolbar}
            slots={props.slots}
            readOnly={readOnly}
            showSend={showSend}
            pending={wiring.pending}
            tray={wiring.tray}
            submit={send}
            policy={policy}
            dispatch={props.dispatch}
            onError={wiring.errors.onError}
          >
            <EditableSurface labels={labels} waiting={waiting} attributes={props.editableProps} />
          </ComposerChrome>
        ) : (
          <EditableSurface labels={labels} waiting={waiting} attributes={props.editableProps} />
        )}
        <GhostText store={props.ghost} />
        <SessionBridge
          session={session}
          preset={preset}
          readOnly={readOnly}
          urlPolicy={setup.urlPolicy}
          tokens={setup.tokens}
          offers={setup.offers}
          onLive={onLive}
          wiring={wiring}
          collaboration={collaboration}
        />
        {collaboration !== undefined && (
          <ChunkBoundary onError={wiring.errors.onError}>
            <CollaborationBridge
              collaboration={collaboration}
              session={session}
              readOnly={readOnly}
              showStored={setup.showStored}
            />
          </ChunkBoundary>
        )}
        <SelectionToolbar
          labels={labels}
          urlPolicy={setup.urlPolicy}
          formats={!(preset === "composer" && props.toolbar === "always" && !readOnly)}
        />
        <SuggestionMenu labels={labels} />
        <PasteNotice labels={labels} />
        {props.attachmentsModule !== null && props.attachments !== undefined && (
          <ChunkBoundary onError={wiring.errors.onError}>
            <AttachmentNotice
              extension={props.attachmentsModule.AttachmentsExtension}
              dismissLabel={labels.dismiss}
            />
          </ChunkBoundary>
        )}
        {props.tablesModule !== null && !readOnly && (
          <ChunkBoundary onError={wiring.errors.onError}>
            <TableControls
              labels={labels}
              extension={props.tablesModule.TablesExtension}
              dispatch={props.dispatch}
            />
          </ChunkBoundary>
        )}
        {props.blockHandles && !readOnly && (
          <BlockControls labels={labels} preset={preset} dispatch={props.dispatch} />
        )}
      </LexicalExtensionComposer>
    </AttachmentContext>
  );
}

/**
 * The editable element. It is its own component so that only it re-renders when the suggestion
 * menu opens or its active row changes.
 */
function EditableSurface(props: {
  labels: EditorLabels;
  waiting: boolean;
  attributes: EditorProps["editableProps"];
}): JSX.Element {
  const { labels, waiting, attributes } = props;
  const aria = useSuggestionAria();
  // An editor whose shared document has not arrived is empty, and not yet for writing in.
  const placeholder = waiting ? labels.loading : labels.placeholder;
  return (
    <ContentEditable
      {...attributes}
      className="ze-content"
      aria-label={labels.editor}
      aria-busy={waiting || undefined}
      aria-placeholder={placeholder}
      ariaAutoComplete={aria.ariaAutoComplete}
      ariaControls={aria.ariaControls}
      ariaActiveDescendant={aria.ariaActiveDescendant}
      placeholder={
        <div className="ze-placeholder" aria-hidden="true">
          {placeholder}
        </div>
      }
    />
  );
}

interface SessionBridgeProps {
  session: DocumentSession;
  preset: EditorPreset;
  readOnly: boolean;
  urlPolicy: UrlPolicy;
  tokens: TokenRegistry | undefined;
  offers: CapabilityOverrides;
  onLive: (live: LiveEditor | null) => void;
  wiring: Wiring;
  collaboration: Collaboration | undefined;
}

/**
 * How to empty each composer session's draft right now: through the editor attached to it, while
 * there is one. A send the host acknowledges after its editor is gone still clears the draft, in
 * the next editor for that session or, with none, in what the session stores; otherwise the sent
 * message comes back as the draft and can be sent twice.
 */
const draftClearers = new WeakMap<DocumentSession, () => void>();

function clearSessionDraft(session: DocumentSession): void {
  const clear = draftClearers.get(session);
  if (clear !== undefined) return clear();
  const empty = createEmptyDocument(session.documentId, session.getDocument().engine.version);
  const detach = session.attach(() => empty);
  session.notifyChange();
  detach();
}

/** Connects the live editor to the session and the imperative handle. Renders nothing. */
function SessionBridge(props: SessionBridgeProps): null {
  const { session, preset, readOnly, urlPolicy, tokens, offers, onLive, wiring } = props;
  const shared = props.collaboration !== undefined;
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // A shared editor is editable once its document has arrived, which its own bridge decides.
    if (!shared) editor.setEditable(!readOnly);
  }, [editor, readOnly, shared]);

  useEffect(() => {
    const live = createLiveEditor({
      editor,
      documentId: session.documentId,
      preset,
      urlPolicy,
      ...(tokens === undefined ? {} : { tokens }),
      offers,
      getEditSequence: () => session.getState().editSequence,
      submit: wiring.submit,
      tray: wiring.tray,
    });
    onLive(live);
    const offSelection = registerSelectionState(editor, (state) =>
      wiring.callbacks.onSelectionChange?.(state),
    );
    const settler = createSettler(editor);
    // True while a stored copy is replacing the content. That is a load, like the first one, and
    // must not reach the session as an edit: nothing to save, nothing to write back.
    let replacing = false;
    const detach = session.attach({
      snapshot: live.snapshot,
      settled: settler.settled,
      // Replacing the content of a shared editor would be an edit, sent to everyone. It refuses,
      // and is rebuilt, which joins the shared document again and changes nothing in it.
      replace(document, policy) {
        if (shared) return false;
        replacing = true;
        try {
          return replaceContent(editor, document.content, policy, wiring.errors.watch);
        } finally {
          replacing = false;
        }
      },
    });
    const offUpdate = editor.registerUpdateListener((update) => {
      const { dirtyElements, dirtyLeaves, tags } = update;
      // Selection-only updates dirty nothing. The initial load is never seen here: it is applied
      // while the editor is built, before this listener exists.
      if (dirtyElements.size === 0 && dirtyLeaves.size === 0) return;
      const editSequence = replacing ? session.getState().editSequence : session.notifyChange();
      const onChange = wiring.callbacks.onChange;
      if (onChange === undefined) return;
      onChange({
        documentId: session.documentId,
        editSequence,
        origin: replacing ? "external" : originFromTags(tags),
        changedBlockIds: $changedBlockIds(update),
      });
    });

    const clearHere = (): void => clearDraft(editor);
    if (preset === "composer") {
      draftClearers.set(session, clearHere);
      wiring.setSubmit(
        createComposerSubmit({
          clearDraft: () => clearSessionDraft(session),
          pending: wiring.pending,
          message: () =>
            readComposerMessage(editor, session.documentId, wiring.tray?.getState().attachments),
          getEditSequence: () => session.getState().editSequence,
          tray: wiring.tray,
          callbacks: () => wiring.callbacks,
        }),
      );
    }

    return () => {
      offSelection();
      settler.dispose();
      offUpdate();
      detach();
      if (draftClearers.get(session) === clearHere) draftClearers.delete(session);
      wiring.setSubmit(null);
      wiring.pending.set(false);
      onLive(null);
    };
  }, [editor, session, preset, urlPolicy, tokens, offers, onLive, wiring, shared]);

  return null;
}

const never = (): (() => void) => () => {};
const always = (): boolean => true;

/** Whether there is a document to write in: always, unless it is a shared one still on its way. */
function useCollaborationReady(collaboration: Collaboration | undefined): boolean {
  return useSyncExternalStore(
    collaboration?.subscribe ?? never,
    collaboration === undefined ? always : () => collaboration.getState().ready,
    always,
  );
}
